/**
 * Inscription VIP Manager - Moon Moon Agency
 * Inscrit le profil (prénom + e-mail) dans une liste Klaviyo via l'API Client Subscriptions,
 * puis affiche le message de succès à la place du formulaire.
 * Doc : https://developers.klaviyo.com/en/reference/create_client_subscription
 */

/*********************************************************************************************
 * 💌 INSCRIPTION VIP MANAGER
 */
const InscriptionVipManager = {
    config: {
        endpoint: 'https://a.klaviyo.com/client/subscriptions',
        revision: '2026-10-15',
        // Événement GTM poussé dans le dataLayer à l'inscription confirmée (form_source distingue les formulaires)
        trackingEvent: 'inscription_evenement',
        selectors: {
            section: '[data-inscription-vip]',
            form: '[data-inscription-vip-form]',
            submit: '[data-inscription-vip-submit]',
            error: '[data-inscription-vip-error]',
            success: '[data-inscription-vip-success]',
            successTitle: '[data-inscription-vip-success-title]',
        },
    },

    /**
     * Initialize the module
     */
    init() {
        if (!window.getElement(this.config.selectors.section)) return;

        this._bindEvents();
        window.log('info', '💌 [INSCRIPTION_VIP]', 'Initialized');
    },

    _bindEvents() {
        document.addEventListener('submit', (e) => {
            if (!e.target.matches(this.config.selectors.form)) return;
            e.preventDefault();
            this.subscribe(e.target);
        });
    },

    async subscribe(form) {
        const section = form.closest(this.config.selectors.section);
        const submit = form.querySelector(this.config.selectors.submit);
        const error = form.querySelector(this.config.selectors.error);
        const { companyId, listId, source } = section.dataset;

        if (submit.getAttribute('aria-busy') === 'true') return;

        if (!companyId || !listId) {
            window.log('error', '💌 [INSCRIPTION_VIP]', 'Klaviyo public key or list ID missing in section settings');
            error.textContent = section.dataset.errorMessage;
            return;
        }

        submit.setAttribute('aria-busy', 'true');
        error.textContent = '';

        const data = new FormData(form);

        try {
            const response = await fetch(`${this.config.endpoint}?company_id=${encodeURIComponent(companyId)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/vnd.api+json',
                    revision: this.config.revision,
                },
                body: JSON.stringify(this._payload(data, listId, section.dataset)),
            });

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.errors?.[0]?.detail || `HTTP ${response.status}`);
            }

            this._showSuccess(section, form);
            this._track(listId, source);
            this._createCustomer(data, section.dataset.customerTag);
            window.emit('global:inscription-vip-subscribed', { listId });
        } catch (err) {
            window.log('error', '💌 [INSCRIPTION_VIP]', 'Klaviyo subscription failed', { error: err.message });
            error.textContent = section.dataset.errorMessage;
        } finally {
            submit.setAttribute('aria-busy', 'false');
        }
    },

    /**
     * Propriété personnalisée du profil (réglages de la section), envoyée seulement si nom et valeur sont remplis.
     * Synchronisée vers un metafield client Shopify par l'intégration Klaviyo → Shopify si elle l'inclut.
     */
    _properties({ propertyName, propertyValue }) {
        if (!propertyName || !propertyValue) return undefined;
        return { [propertyName]: propertyValue };
    },

    _payload(data, listId, { source, ...settings }) {
        return {
            data: {
                type: 'subscription',
                attributes: {
                    custom_source: source || undefined,
                    profile: {
                        data: {
                            type: 'profile',
                            attributes: {
                                email: data.get('email').trim(),
                                first_name: data.get('first_name').trim(),
                                properties: this._properties(settings),
                                subscriptions: {
                                    email: { marketing: { consent: 'SUBSCRIBED' } },
                                },
                            },
                        },
                    },
                },
                relationships: {
                    list: { data: { type: 'list', id: listId } },
                },
            },
        };
    },

    /**
     * Fiche client Shopify via le formulaire client natif (/contact, form_type customer), comme une inscription newsletter :
     * créée seulement si l'e-mail n'a pas encore de fiche, avec le tag des réglages. Le Flow Shopify « Client créé » qui teste
     * ce tag y écrit le champ méta VIP. Sans attente ni blocage : l'inscription Klaviyo fait foi.
     */
    _createCustomer(data, tag) {
        if (!tag) return;

        const body = new URLSearchParams({
            form_type: 'customer',
            utf8: '✓',
            'contact[email]': data.get('email').trim(),
            'contact[first_name]': data.get('first_name').trim(),
            'contact[tags]': tag,
        });

        // keepalive : la requête aboutit même si la cliente quitte la page via le bouton du message de succès
        fetch(`${window.Shopify?.routes?.root || '/'}contact`, { method: 'POST', body, keepalive: true })
            .then((response) => {
                // Protection anti-spam de Shopify : redirection vers /challenge, la fiche n'est pas créée
                if (response.url.includes('/challenge')) {
                    window.log('warn', '💌 [INSCRIPTION_VIP]', 'Shopify customer form challenged, customer not created');
                }
            })
            .catch((err) => {
                window.log('error', '💌 [INSCRIPTION_VIP]', 'Shopify customer creation failed', { error: err.message });
            });
    },

    /**
     * Push GTM : le formulaire n'étant pas un formulaire Klaviyo natif, l'événement klaviyoForms ne se déclenche pas.
     * Aucune donnée personnelle envoyée.
     */
    _track(listId, source) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: this.config.trackingEvent,
            formData: {
                form_type: 'submit',
                form_list_id: listId,
                form_source: source || '',
            },
        });
    },

    _showSuccess(section, form) {
        form.hidden = true;
        section.querySelector(this.config.selectors.success).hidden = false;
        section.dataset.state = 'success';
        section.querySelector(this.config.selectors.successTitle)?.focus();
    },
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => InscriptionVipManager.init());
} else {
    InscriptionVipManager.init();
}
